import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useNavigate, useParams } from 'react-router-dom';
import { OptionType } from 'types';
import {
  IVAdjustmentSettings,
  OC_DEFAULT_IV_SETTINGS,
  OC_DEFAULT_SYM,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import {
  shortIdGenerator,
  stringifyLeg,
  stringifyIVSettings,
  parseLegString,
  stringifyShare,
  parseShareString,
  parseIVSettings,
} from 'util/optionsCalculator';
import { dayjs } from 'util/shared/date';
import { useMemo, useCallback } from 'react';

const defaultLegs: PositionLeg[] = [
  {
    id: shortIdGenerator(),
    type: OptionType.CALL,
    strike: 560,
    quantity: 1,
    expiration: dayjs('2025-09-19')
      .tz('America/New_York')
      .hour(16)
      .minute(0)
      .second(0)
      .millisecond(0)
      .valueOf(),
    price: 12.35,
    sym: 'SPY',
  },
  {
    id: shortIdGenerator(),
    type: OptionType.CALL,
    strike: 570,
    quantity: -2,
    expiration: dayjs('2025-09-19')
      .tz('America/New_York')
      .hour(16)
      .minute(0)
      .second(0)
      .millisecond(0)
      .valueOf(),
    price: 7.8,
    sym: 'SPY',
  },
  {
    id: shortIdGenerator(),
    type: OptionType.CALL,
    strike: 580,
    quantity: 1,
    expiration: dayjs('2025-09-19')
      .tz('America/New_York')
      .hour(16)
      .minute(0)
      .second(0)
      .millisecond(0)
      .valueOf(),
    price: 4.25,
    sym: 'SPY',
  },
];

const defaultBaseDate = dayjs().valueOf();
const defaultLegObjects = defaultLegs.map(stringifyLeg);
const defaultIvSettingsStrings = [
  stringifyIVSettings(defaultLegs[0].expiration, OC_DEFAULT_IV_SETTINGS),
];

// this is the main hook for the options calculator state
const useOptionsCalculator = () => {
  const navigate = useNavigate();
  const { sym = OC_DEFAULT_SYM } = useParams<{ sym: string }>();
  const [baseDate, setBaseDate] = useQueryState(
    'bd',
    parseAsInteger.withDefault(defaultBaseDate),
  );

  const [legStrings, setLegStrings] = useQueryState(
    'legs',
    parseAsArrayOf(parseAsString).withDefault(defaultLegObjects),
  );

  const [sharesString, setSharesString] = useQueryState(
    'shares',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const [ivSettingsString, setIvSettingsString] = useQueryState(
    'ivs',
    parseAsArrayOf(parseAsString).withDefault(defaultIvSettingsStrings),
  );

  // Memoize expensive parsing operations
  const legs = useMemo(() => {
    return legStrings.map(parseLegString).filter(Boolean) as PositionLeg[];
  }, [legStrings]);

  const shares = useMemo(() => {
    return sharesString
      .map(parseShareString)
      .filter(Boolean) as PositionShares[];
  }, [sharesString]);

  const expirationsIVSettingsMap = useMemo(() => {
    return new Map(
      ivSettingsString
        .map(parseIVSettings)
        .filter((s): s is [number, IVAdjustmentSettings] => s != null),
    );
  }, [ivSettingsString]);

  const updateLegsBulk = useCallback(
    (legIds: string[], updatedFields: Partial<PositionLeg>) => {
      const newLegStrings = legStrings.map((legStr) => {
        const leg = parseLegString(legStr);
        if (!leg) {
          return legStr;
        }
        if (legIds.includes(leg.id)) {
          const updatedLeg = { ...leg, ...updatedFields };
          return stringifyLeg(updatedLeg);
        }
        return stringifyLeg(leg);
      });
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  // Memoize callback functions to prevent unnecessary re-renders
  const handleSymChange = useCallback(
    (newSym: string) => {
      updateLegsBulk(
        legStrings
          .map((legStr) => parseLegString(legStr)?.id)
          .filter((id): id is string => id != null),
        { sym: newSym },
      );
      navigate(`/optionsCalculator/${newSym}`);
    },
    [legStrings, navigate, updateLegsBulk],
  );

  const addLeg = useCallback(
    (leg: PositionLeg) => {
      const legWithId = {
        ...leg,
        id: shortIdGenerator(),
      };
      const newLegStrings = [...legStrings, stringifyLeg(legWithId)];
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const removeLeg = useCallback(
    (legId: string) => {
      const newLegStrings = legStrings.filter(
        (legStr) => !legStr.endsWith(`_${legId}`),
      );
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const removeLegsBulk = useCallback(
    (legIds: string[]) => {
      const newLegStrings = legStrings.filter(
        (legStr) => !legIds.some((id) => legStr.endsWith(`_${id}`)),
      );
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const syncIVSettingsWithLegs = useCallback(
    (legStrings: string[]) => {
      const givenLegs = legStrings
        .map(parseLegString)
        .filter(Boolean) as PositionLeg[];
      const expirations = givenLegs
        .map((leg) => leg.expiration)
        .filter((expiration): expiration is number => expiration != null);
      const newIvSettingsString = expirations.map((expiration) => {
        if (expirationsIVSettingsMap.has(expiration)) {
          return stringifyIVSettings(
            expiration,
            expirationsIVSettingsMap.get(expiration) ?? OC_DEFAULT_IV_SETTINGS,
          );
        }
        return stringifyIVSettings(expiration, OC_DEFAULT_IV_SETTINGS);
      });
      void setIvSettingsString(newIvSettingsString);
    },
    [expirationsIVSettingsMap, setIvSettingsString],
  );

  const updateLeg = useCallback(
    (legId: string, updatedFields: Partial<PositionLeg>) => {
      const newLegStrings = legStrings.map((legStr) => {
        if (legStr.endsWith(`_${legId}`)) {
          const oldLeg = parseLegString(legStr);
          if (!oldLeg) {
            return legStr;
          }
          const updatedLeg = { ...oldLeg, ...updatedFields };
          return stringifyLeg(updatedLeg);
        }
        return legStr;
      });
      syncIVSettingsWithLegs(newLegStrings);
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings, syncIVSettingsWithLegs],
  );

  const clearLegs = useCallback(() => {
    void setLegStrings([]);
  }, [setLegStrings]);

  const updateShares = useCallback(
    (shares: PositionShares[]) => {
      void setSharesString(shares.map(stringifyShare));
    },
    [setSharesString],
  );

  const updateExpirationsIVSettingsMap = useCallback(
    (
      expiration: PositionLeg['expiration'],
      ivSettings: IVAdjustmentSettings,
    ) => {
      const newIvSettingsStrings = ivSettingsString.map((settingsStr) => {
        if (settingsStr.startsWith(`${expiration}:`)) {
          return stringifyIVSettings(expiration, ivSettings);
        }
        return settingsStr;
      });

      void setIvSettingsString(newIvSettingsStrings);
    },
    [ivSettingsString, setIvSettingsString],
  );

  const updateBaseDate = useCallback(
    (newBaseDate: number) => {
      void setBaseDate(newBaseDate);
    },
    [setBaseDate],
  );

  // Return memoized object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      sym,
      baseDate,
      legs,
      shares,
      expirationsIVSettingsMap,
      handleSymChange,
      updateExpirationsIVSettingsMap,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
    }),
    [
      sym,
      baseDate,
      legs,
      shares,
      expirationsIVSettingsMap,
      handleSymChange,
      updateExpirationsIVSettingsMap,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
    ],
  );
};

export default useOptionsCalculator;
